import type { DateTime } from "luxon";
import { useChosenDepartment } from "../contexts/ChosenDepartment";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import type { DepartmentWageDataResponse, Nurse } from "../utils/models";
import { panelWindowOf } from "../utils/panelWeeks";
import { useRosterLookup } from "./useRosterLookup";
import { useScheduleWages } from "./useScheduleWages";
import { useScheduleCacheEnabled } from "./useScheduleWeekRules";
import { type ScheduleWeeks, useScheduleWeeks } from "./useScheduleWeeks";

export type DayStatsInputs = ScheduleWeeks & {
	/** The people the day stats read (wages, unions, break rules): the old list's with the cache off, the roster's on. */
	nurses: Nurse[];
	getNurseById: (id: number) => Nurse | undefined;
	/** The day wage totals the stats read: the old list's with the cache off, the cached weeks' on. */
	wageData: DepartmentWageDataResponse | null;
};

// One object, so a roster that isn't in yet doesn't hand out a new array on every render.
const NO_NURSES: Nurse[] = [];

/**
 * `useScheduleWeeks` around `date`, plus the people and wages a day's stats (`calculateDayStats`) are computed from:
 * what a screen needs to show a day's HPPD, PPD and cost. With `staffing_schedule_cache` on they come from the cache
 * and the roster; off, from the old list, exactly as before.
 */
export const useDayStatsInputs = (date: DateTime): DayStatsInputs => {
	const { hospitalData, getNurseById, wageData } = useHospitalData();
	const { chosenDepartment } = useChosenDepartment();
	const isCacheEnabled = useScheduleCacheEnabled();
	const schedule = useScheduleWeeks(date);
	const { from, to } = panelWindowOf(date);
	const wages = useScheduleWages(from, to);
	const roster = useRosterLookup(chosenDepartment.department_id);

	if (!isCacheEnabled) return { ...schedule, nurses: hospitalData.nurses, getNurseById, wageData };
	// DEFAULT: no one until the roster arrives, while `isLoading` is true; the weeks and the stats read `people` meanwhile.
	return { ...schedule, nurses: roster.roster ?? NO_NURSES, getNurseById: roster.getNurseById, wageData: wages };
};
